import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import EmptyState from "@/components/feature/EmptyState";
import { useFavorites } from "@/hooks/useFavorites";
import { getCategoryName, getComicById, getRelatedComics } from "@/utils/comic";

export default function ComicDetailPage() {
  const { comicId } = useParams();
  const comic = getComicById(comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [descOpen, setDescOpen] = useState(false);
  const [orderDesc, setOrderDesc] = useState(true);
  const [toast, setToast] = useState("");

  if (!comic) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="所请求漫画不存在或已下架"
          description="该作品可能已经调整或暂时下线，去首页看看其他精彩漫画吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const favorited = isFavorite(comic.id);
  const chapters = orderDesc ? [...comic.chapters].reverse() : comic.chapters;
  const related = getRelatedComics(comic, 4);
  const latest = comic.chapters[comic.chapters.length - 1];

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleFavorite = () => {
    const next = toggleFavorite(comic.id);
    showToast(next ? "已加入我的收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快分享给同好吧");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full">
      <section className="relative w-full pt-24 md:pt-28 pb-10 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={comic.banner}
            alt=""
            className="w-full h-full object-cover object-top blur-sm"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 to-foreground-950/70"></div>

        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="text-background-200">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: `${getCategoryName(comic.categoryIds[0])}漫画`, to: `/category/${comic.categoryIds[0]}` },
                { label: comic.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-[150px] md:w-[210px] shrink-0 aspect-[3/4] rounded-lg overflow-hidden bg-background-800 mx-auto md:mx-0">
              <img
                src={comic.cover}
                alt={`${comic.title} 漫画封面`}
                title={`${comic.title} - 蛙漫漫画详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0 text-background-50">
              <h1 className="text-2xl md:text-3xl font-label font-bold">
                {comic.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs md:text-sm">
                <Link
                  to={`/author/${comic.authorId}`}
                  className="px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
                >
                  <i className="ri-user-line mr-1"></i>
                  {comic.authorName}
                </Link>
                <span className="px-3 py-1 rounded-full bg-primary-500/80">
                  {comic.status}
                </span>
                <span className="px-3 py-1 rounded-full bg-white/15">
                  <i className="ri-star-fill text-accent-400 mr-1"></i>
                  {comic.rating.toFixed(1)} 分
                </span>
                <span className="px-3 py-1 rounded-full bg-white/15">
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views} 阅读
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {comic.categoryIds.map((id) => (
                  <Link
                    key={id}
                    to={`/category/${id}`}
                    className="px-2.5 py-1 rounded-md text-xs bg-accent-500/90 text-accent-950 font-label font-bold hover:bg-accent-400 transition-colors"
                  >
                    {getCategoryName(id)}
                  </Link>
                ))}
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md text-xs bg-white/10 text-background-100"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 text-sm text-background-100/90">
                <button
                  type="button"
                  onClick={() => setDescOpen((v) => !v)}
                  className="flex items-center gap-1 font-label font-bold mb-2 cursor-pointer"
                >
                  作品简介
                  <i className={descOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </button>
                <p className={`leading-relaxed ${descOpen ? "" : "line-clamp-3"}`}>
                  {comic.description}
                </p>
              </div>

              <div className="mt-3 text-xs text-background-200/80">
                最近更新：{comic.updatedAt} · {comic.latestChapter}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/read/${comic.id}/${latest?.id ?? "1"}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold whitespace-nowrap transition-colors"
                >
                  <i className="ri-book-open-line"></i>
                  开始阅读
                </Link>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer ${
                    favorited
                      ? "bg-accent-500 text-accent-950"
                      : "bg-white/15 text-background-50 hover:bg-white/25"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-white/15 text-background-50 text-sm font-label font-bold hover:bg-white/25 whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg md:text-xl font-label font-bold text-foreground-950">
              章节目录
            </h2>
            <p className="text-xs md:text-sm text-foreground-500 mt-1">
              共 {comic.chapters.length} 话 · {comic.status}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOrderDesc((v) => !v)}
            className="inline-flex items-center gap-1 h-9 px-4 rounded-md bg-background-100 text-xs md:text-sm font-label font-bold text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-sort-desc"></i>
            {orderDesc ? "最新在前" : "最早在前"}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
          {chapters.map((chapter) => (
            <Link
              key={chapter.id}
              to={`/read/${comic.id}/${chapter.id}`}
              className="group flex items-center justify-between gap-2 px-3 py-3 rounded-md bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <span className="text-xs md:text-sm text-foreground-800 line-clamp-1 group-hover:text-primary-700">
                {chapter.title}
              </span>
              <i className="ri-play-circle-line text-primary-500 shrink-0"></i>
            </Link>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-6 pb-12">
          <h2 className="text-lg md:text-xl font-label font-bold text-foreground-950 mb-5">
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm font-label animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}